import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/home/CTASection";
import { Calculator, CurrencyDollar, Boat, ArrowRight } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";

function ShippingCalculator() {
  const [form, setForm] = useState({ destination: "USA", mode: "sea_lcl", weight_kg: 100, volume_cbm: 2 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const calc = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tools/shipping-estimate", {
        ...form, weight_kg: Number(form.weight_kg), volume_cbm: Number(form.volume_cbm),
      });
      setResult(data);
    } catch { toast.error("Could not calculate estimate"); } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="shipping-calculator">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white"><Boat size={22} weight="duotone" /></div>
        <h3 className="font-heading text-xl font-semibold text-ink">Shipping Quote Estimator</h3>
      </div>
      <form onSubmit={calc} className="mt-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">Destination</label>
            <select value={form.destination} onChange={set("destination")} data-testid="ship-destination" className={field}>
              {["USA", "UK", "UAE", "Nigeria", "Ghana", "Kenya"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">Shipping mode</label>
            <select value={form.mode} onChange={set("mode")} data-testid="ship-mode" className={field}>
              <option value="sea_lcl">Sea — LCL</option>
              <option value="sea_fcl">Sea — FCL (20ft)</option>
              <option value="air">Air Freight</option>
              <option value="express">Express</option>
            </select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">Weight (kg)</label>
            <input type="number" value={form.weight_kg} onChange={set("weight_kg")} data-testid="ship-weight" className={field} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">Volume (CBM)</label>
            <input type="number" step="0.1" value={form.volume_cbm} onChange={set("volume_cbm")} data-testid="ship-volume" className={field} />
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="ship-calculate" className="w-full rounded-md bg-ink px-6 py-3.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-60">
          {loading ? "Calculating…" : "Estimate Cost"}
        </button>
      </form>
      {result && (
        <div className="mt-6 rounded-xl bg-mist p-6" data-testid="ship-result">
          <p className="text-xs uppercase tracking-wider font-semibold text-teal">Estimated freight cost</p>
          <p className="mt-1 font-heading text-3xl font-bold text-ink">${result.estimate_usd_low.toLocaleString()} – ${result.estimate_usd_high.toLocaleString()}</p>
          <p className="mt-2 text-sm text-slate-600">Transit time: <span className="font-semibold text-ink">{result.transit_time}</span></p>
          <p className="mt-3 text-xs text-slate-400">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

function CurrencyConverter() {
  const [currencies, setCurrencies] = useState(["USD", "CNY", "EUR", "GBP"]);
  const [form, setForm] = useState({ amount: 1000, from_currency: "USD", to_currency: "CNY" });
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/tools/currencies").then((r) => setCurrencies(r.data.currencies)).catch(() => {}); }, []);

  const convert = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tools/currency-convert", { ...form, amount: Number(form.amount) });
      setResult(data);
    } catch { toast.error("Conversion failed"); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="currency-converter">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white"><CurrencyDollar size={22} weight="duotone" /></div>
        <h3 className="font-heading text-xl font-semibold text-ink">Currency Converter</h3>
      </div>
      <form onSubmit={convert} className="mt-6 space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-slate-500">Amount</label>
          <input type="number" value={form.amount} onChange={set("amount")} data-testid="cur-amount" className={field} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">From</label>
            <select value={form.from_currency} onChange={set("from_currency")} data-testid="cur-from" className={field}>
              {currencies.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">To</label>
            <select value={form.to_currency} onChange={set("to_currency")} data-testid="cur-to" className={field}>
              {currencies.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" data-testid="cur-convert" className="w-full rounded-md bg-ink px-6 py-3.5 text-sm font-semibold text-white hover:bg-ink-800">Convert</button>
      </form>
      {result && (
        <div className="mt-6 rounded-xl bg-mist p-6" data-testid="cur-result">
          <p className="font-heading text-3xl font-bold text-ink">{result.result.toLocaleString()} {result.to}</p>
          <p className="mt-2 text-sm text-slate-500">1 {result.from} = {result.rate} {result.to}</p>
          <p className="mt-3 text-xs text-slate-400">Indicative reference rates. Confirm live rates before payment.</p>
        </div>
      )}
    </div>
  );
}

export default function Tools() {
  return (
    <div data-testid="tools-page">
      <PageHero
        overline="Trade tools"
        title="Plan your import in seconds"
        subtitle="Estimate freight costs and convert currencies instantly with our free trade calculators."
        crumbs={[{ label: "Tools" }]}
        image="https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg"
      />
      <section className="bg-mist py-24 lg:py-28">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 lg:grid-cols-2 lg:px-8">
          <ShippingCalculator />
          <CurrencyConverter />
        </div>
      </section>
      <CTASection />
    </div>
  );
}
